import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Check, Star, Zap, Shield, Award, ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PricingTier {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  period: string;
  popular?: boolean;
  features: string[];
  cta: string;
  savings?: string;
  badge?: string;
}

const pricingTiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Free Trial',
    description: 'Perfect for testing the waters',
    price: 0,
    period: '7 days',
    features: [
      '30 minutes daily usage',
      'Basic voice selection',
      'Standard accuracy',
      'Export to TXT',
      'Email support',
    ],
    cta: 'Start Free Trial',
  },
  {
    id: 'pro',
    name: 'Professional',
    description: 'For serious productivity gains',
    price: 19,
    originalPrice: 39,
    period: 'month',
    popular: true,
    badge: 'Most Popular',
    savings: 'Save 50% - Limited Time',
    features: [
      'Unlimited usage',
      'All premium voices',
      '99.9% accuracy guarantee',
      'Export to DOC, PDF, TXT',
      'Voice commands',
      'Custom vocabulary',
      'Priority support',
      'Offline mode',
    ],
    cta: 'Get Pro Now',
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    description: 'One-time payment, lifetime access',
    price: 199,
    originalPrice: 499,
    period: 'once',
    savings: 'Save $300',
    badge: 'Best Value',
    features: [
      'Everything in Pro',
      'Lifetime updates',
      'Early access to new features',
      'API access',
      'Team collaboration (5 seats)',
      'Custom voice training',
      'White-glove support',
      'Commercial license',
    ],
    cta: 'Get Lifetime Access',
  },
];

export function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/5 to-background" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Simple Pricing</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold">
              Start Free, Upgrade When Ready
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Join 50,000+ professionals saving hours every day. 
              No hidden fees, cancel anytime.
            </p>

            {/* Limited Time Offer Banner */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 px-6 py-3 rounded-full mt-4">
              <Zap className="h-5 w-5 text-orange-500" />
              <span className="font-semibold text-orange-500">Flash Sale:</span>
              <span className="text-foreground">50% OFF all plans - Ends in 48 hours!</span>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {pricingTiers.map((tier, index) => (
              <Card
                key={tier.id}
                className={cn(
                  "relative overflow-hidden transition-all duration-300",
                  tier.popular && "border-primary shadow-lg shadow-primary/20 scale-105",
                  "hover:shadow-xl hover:-translate-y-1",
                  "animate-slide-up opacity-0"
                )}
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
              >
                {/* Popular Badge */}
                {tier.badge && (
                  <div className="absolute -top-4 -right-4 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-xs font-bold px-8 py-4 rotate-12 shadow-lg">
                    {tier.badge}
                  </div>
                )}

                <CardHeader className="pb-4">
                  <div className="space-y-2">
                    <CardTitle className="text-2xl">{tier.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{tier.description}</p>
                  </div>

                  {/* Pricing */}
                  <div className="pt-4">
                    <div className="flex items-baseline gap-2">
                      {tier.originalPrice && (
                        <span className="text-2xl text-muted-foreground line-through">
                          ${tier.originalPrice}
                        </span>
                      )}
                      <span className="text-5xl font-bold">
                        ${tier.price}
                      </span>
                      <span className="text-muted-foreground">/{tier.period}</span>
                    </div>
                    {tier.savings && (
                      <p className="text-sm text-green-600 font-medium mt-1">
                        {tier.savings}
                      </p>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Features */}
                  <ul className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    className={cn(
                      "w-full group",
                      tier.popular && "shadow-lg"
                    )}
                    size="lg"
                    variant={tier.popular ? "default" : "outline"}
                  >
                    {tier.cta}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>

                  {/* Trust Indicators */}
                  {tier.popular && (
                    <div className="pt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        <span>30-day guarantee</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-current" />
                        <span>4.9/5 rating</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Trust Section */}
          <div className="text-center space-y-8">
            {/* Testimonial */}
            <Card className="max-w-2xl mx-auto border-muted/50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                  ))}
                </div>
                <blockquote className="text-lg italic mb-4">
                  "SpeakFlow has completely transformed how I write. I'm 3x more productive 
                  and my wrists thank me every day. Worth every penny!"
                </blockquote>
                <cite className="text-sm text-muted-foreground">
                  - Sarah Chen, Content Creator
                </cite>
              </CardContent>
            </Card>

            {/* Guarantees */}
            <div className="flex flex-wrap justify-center gap-8">
              <div className="flex items-center gap-2">
                <Shield className="h-8 w-8 text-green-500" />
                <div className="text-left">
                  <p className="font-semibold">30-Day Money Back</p>
                  <p className="text-sm text-muted-foreground">No questions asked</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-8 w-8 text-blue-500" />
                <div className="text-left">
                  <p className="font-semibold">Lifetime Updates</p>
                  <p className="text-sm text-muted-foreground">Always improving</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="h-8 w-8 text-purple-500" />
                <div className="text-left">
                  <p className="font-semibold">Instant Access</p>
                  <p className="text-sm text-muted-foreground">Start in seconds</p>
                </div>
              </div>
            </div>

            {/* FAQ Link */}
            <p className="text-muted-foreground">
              Have questions? Check our <a href="#faq" className="text-primary hover:underline">FAQ</a> or 
              contact our <a href="#" className="text-primary hover:underline">support team</a>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}